import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { 
  KeyRound, 
  Mail, 
  ArrowRight, 
  ArrowLeft,
  Shield,
  Zap,
  CheckCircle,
  Loader2,
  Info
} from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [tokenSent, setTokenSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/forgot-password', { email });
      
      toast.success('Reset Token Generated!', {
        description: 'Check the response for your reset token (in production, this would be sent to your email)',
        duration: 6000,
      });
      
      // For development, we show the token directly
      if (response.data.token) {
        setResetToken(response.data.token);
        setTokenSent(true);
      }
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error sending reset email';
      toast.error('Error', {
        description: message,
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToReset = () => {
    navigate(`/reset-password?token=${resetToken}`);
  };

  const isFormValid = email.trim() !== '';

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-slate-800/30"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className={`w-full max-w-md transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          
          {/* Logo and Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-2xl shadow-blue-500/25 mb-6">
              <KeyRound className="h-12 w-12 text-white drop-shadow-lg" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                APT-AccessHub
              </h1>
              <p className="text-blue-200/80 text-lg">
                Reset your password
              </p>
            </div>
            
            {/* Feature highlights */}
            <div className="flex justify-center space-x-6 mt-6">
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Shield className="h-4 w-4" />
                <span className="text-sm">Secure</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Zap className="h-4 w-4" />
                <span className="text-sm">Fast</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Reliable</span>
              </div>
            </div>
          </div>

          {/* Forgot Password Form */}
          <Card className="backdrop-blur-xl bg-white/10 border-white/20 shadow-2xl shadow-black/20">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-semibold text-white">
                {tokenSent ? 'Reset Token Generated' : 'Forgot Password'}
              </CardTitle>
              <p className="text-blue-200/70 text-sm mt-2">
                {tokenSent 
                  ? 'Your password reset token has been generated'
                  : 'Enter your email to receive a password reset link'
                }
              </p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {!tokenSent ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/90 font-medium">
                      Email Address
                    </Label>
                    <div className="relative group">
                      <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                        focusedField === 'email' ? 'text-blue-400' : 'text-white/50'
                      }`} />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className="pl-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-blue-400 focus:ring-blue-400/20 transition-all duration-200"
                        placeholder="you@company.com"
                        required
                      />
                      <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
                        focusedField === 'email' ? 'w-full' : 'w-0'
                      }`}></div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className={`w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform ${
                      !loading && isFormValid ? 'hover:scale-105 hover:shadow-xl hover:shadow-blue-500/25' : ''
                    } ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Sending reset link...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span>Send Reset Link</span>
                        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                      </div>
                    )}
                  </Button>
                </form>
              ) : (
                /* Token Generated View */
                <div className="space-y-6">
                  <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Info className="h-5 w-5 text-blue-400 mt-0.5" />
                      <div>
                        <h4 className="text-white font-medium">Development Mode</h4>
                        <p className="text-blue-200/80 text-sm mt-1">
                          In production, this token would be sent to your email. For testing, use the token below:
                        </p>
                        <div className="mt-3 p-2 bg-slate-800/50 rounded border text-xs text-blue-300 font-mono break-all">
                          {resetToken}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleProceedToReset}
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-green-500/25"
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span>Proceed to Reset Password</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  </Button>
                </div>
              )}

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center space-x-2 text-blue-300 hover:text-blue-200 transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back to Sign In</span>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <div className="flex justify-center items-center space-x-4 text-blue-300/60 text-xs mb-4">
              <div className="flex items-center space-x-1">
                <Shield className="h-3 w-3" />
                <span>AES-256 Encryption</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>SSL Secure</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Zap className="h-3 w-3" />
                <span>99.9% Uptime</span>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="text-blue-300/50 text-xs">
              Powered by <span className="font-medium text-blue-300/70">Budget Digital Solutions LLC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;